/**
 * @file card-lightning.effect.ts
 * Ré-export de l'effet de foudre 2D canvas depuis la source version_finale.
 *
 * Cet alias permet aux composants partagés d'importer `CardLightningEffect`
 * sans dépendre directement d'un chemin de page, facilitant d'éventuels
 * déplacements futurs de la classe source.
 */

/**
 * @file card-lightning-effect.ts
 * 2D canvas lightning effect for cards — direct port of `lightning.ts`
 * (Three.js version) adapted to a plain HTMLCanvasElement.
 *
 * The bolt generation algorithm (`generateBoltPoints`) is identical: a
 * subdivided line from start → end with per-segment random jitter.
 * Bolts radiate from the card centre, fade out as their lifetime expires,
 * and branches spawn randomly just like the booster-opening effect.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Bolt {
	points: { x: number; y: number }[];
	life: number;
	maxLife: number;
	/** Colour of the main stroke. */
	color: string;
	/** Width of the main stroke in CSS pixels. */
	lineWidth: number;
}

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

/**
 * Generates a jagged polyline from `(sx, sy)` to `(ex, ey)`.
 * Mirrors the 3-D `generateBoltPoints` from `lightning.ts`.
 *
 * @param sx       - Start X.
 * @param sy       - Start Y.
 * @param ex       - End X.
 * @param ey       - End Y.
 * @param segments - Number of line segments (more = more detail).
 * @param jitter   - Maximum random offset per intermediate point in px.
 */
function generateBoltPoints(
	sx: number, sy: number,
	ex: number, ey: number,
	segments: number,
	jitter: number,
): Bolt['points'] {
	const pts: Bolt['points'] = [{ x: sx, y: sy }];
	for (let i = 1; i < segments; i++) {
		const t = i / segments;
		pts.push({
			x: sx + (ex - sx) * t + (Math.random() - 0.5) * jitter,
			y: sy + (ey - sy) * t + (Math.random() - 0.5) * jitter,
		});
	}
	pts.push({ x: ex, y: ey });
	return pts;
}

// ---------------------------------------------------------------------------
// Class
// ---------------------------------------------------------------------------

/**
 * Draws procedural lightning on an `HTMLCanvasElement` overlaid on a card
 * front face.  Call `update(delta, active)` every animation frame.
 *
 * When `active` is `false` (card is face-down) the canvas is cleared
 * immediately and no new bolts are spawned.
 */
export class CardLightningEffect {
	/** Amber/gold palette matching the collector rarity. */
	private static readonly COLORS = ['#eab308', '#f97316', '#fde047'] as const;

	private readonly ctx: CanvasRenderingContext2D;
	private bolts: Bolt[] = [];
	private spawnTimer = 0;

	/**
	 * @param canvas - The `<canvas>` element positioned over the card front face.
	 *                 Its `width` and `height` attributes must already be set to
	 *                 the logical card dimensions (e.g. 280 × 410).
	 */
	constructor(private readonly canvas: HTMLCanvasElement) {
		this.ctx = canvas.getContext('2d')!;
		// Mélange additif fait dans le canvas (remplace le mix-blend-mode CSS `screen`,
		// qui obligeait le navigateur à recomposer la carte entière à chaque frame).
		this.ctx.globalCompositeOperation = 'lighter';
	}

	/**
	 * Advances the simulation by `delta` seconds.
	 *
	 * @param delta  - Time elapsed since the last frame in seconds.
	 * @param active - Pass `true` only when the card front face is visible.
	 */
	update(delta: number, active: boolean): void {
		const { width: w, height: h } = this.canvas;

		if (!active) {
			if (this.bolts.length > 0) {
				this.ctx.clearRect(0, 0, w, h);
				this.bolts = [];
			}
			return;
		}

		// ── Spawn ──────────────────────────────────────────────────────────────
		this.spawnTimer += delta;
		// Slower cadence: one burst every 0.25–0.55 s
		const spawnRate = 0.25 + Math.random() * 0.3;
		if (this.spawnTimer > spawnRate) {
			this.spawnTimer = 0;

			// Origin: random point on the card (not always the centre)
			const sx = w * (0.15 + Math.random() * 0.7);
			const sy = h * (0.15 + Math.random() * 0.7);

			const angle = Math.random() * Math.PI * 2;
			const dist  = 40 + Math.random() * Math.min(w, h) * 0.38;
			const ex    = sx + Math.cos(angle) * dist;
			const ey    = sy + Math.sin(angle) * dist;

			const color = CardLightningEffect.COLORS[
				Math.floor(Math.random() * CardLightningEffect.COLORS.length)
				];

			this.bolts.push({
				points:  generateBoltPoints(sx, sy, ex, ey, 8 + Math.floor(Math.random() * 6), 14 + Math.random() * 16),
				life:    0,
				maxLife: 0.08 + Math.random() * 0.12,
				color,
				lineWidth: 1.5,
			});

			// Branch — 40 % chance
			if (Math.random() > 0.6) {
				const t   = 0.3 + Math.random() * 0.4;
				const bsx = sx + (ex - sx) * t;
				const bsy = sy + (ey - sy) * t;
				const bex = bsx + (Math.random() - 0.5) * 70;
				const bey = bsy + (Math.random() - 0.5) * 70;
				this.bolts.push({
					points:  generateBoltPoints(bsx, bsy, bex, bey, 4 + Math.floor(Math.random() * 3), 8),
					life:    0,
					maxLife: 0.06 + Math.random() * 0.06,
					color:   '#fde047',
					lineWidth: 0.8,
				});
			}
		}

		// ── Age ────────────────────────────────────────────────────────────────
		this.bolts = this.bolts.filter(b => { b.life += delta; return b.life < b.maxLife; });

		// ── Draw ───────────────────────────────────────────────────────────────
		this.ctx.clearRect(0, 0, w, h);
		for (const bolt of this.bolts) {
			const opacity = 1 - bolt.life / bolt.maxLife;
			this.ctx.save();
			this.ctx.globalAlpha  = opacity;
			this.ctx.strokeStyle  = bolt.color;
			this.ctx.lineWidth    = bolt.lineWidth;
			this.ctx.lineCap      = 'round';
			this.ctx.lineJoin     = 'round';
			// shadowBlur est coûteux (convolution recalculée à chaque stroke) : réservé aux
			// éclairs principaux, omis sur les branches secondaires plus fines.
			if (bolt.lineWidth >= 1.5) {
				this.ctx.shadowColor = bolt.color;
				this.ctx.shadowBlur  = 10;
			}
			this.ctx.beginPath();
			this.ctx.moveTo(bolt.points[0].x, bolt.points[0].y);
			for (let i = 1; i < bolt.points.length; i++) {
				this.ctx.lineTo(bolt.points[i].x, bolt.points[i].y);
			}
			this.ctx.stroke();
			this.ctx.restore();
		}
	}

	/** Clears the canvas and releases all bolt data. */
	dispose(): void {
		this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
		this.bolts = [];
	}
}
